import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useReducer,
} from 'react';
import { useHttpClient, useNetworkStatus } from '../../contexts';
import { useToast } from '../../hooks';
import {
  OrganizationRapport,
  SurveyCustomerExperienceOptions,
  SurveyMediaChecklistOptions,
} from './appointmentProposalSurveyOptions.models';
import {
  initialState,
  proposalBuilderSurveyOptionsReducer,
} from './appointmentProposalSurveyOptions.reducer';
import { transformRapportOptions } from './utils/rapportUtils';
import {
  mediaChecklistIndoorOptions,
  mediaChecklistOutdoorOptions,
} from './utils/surveyOptions';

interface AppointmentProposalSurveyOptionsContextProps {
  customerExperienceOptions: {
    current: SurveyCustomerExperienceOptions[];
    priorities: SurveyCustomerExperienceOptions[];
  };
  mediaChecklistOptions: SurveyMediaChecklistOptions;
  isLoading: boolean;
  isLoaded: boolean;
  fetchProposalBuilderData: (organizationId: string) => void;
}

interface AppointmentProposalSurveyOptionsProviderProps {
  currentCustomerExperienceOptions: SurveyCustomerExperienceOptions[];
  customerExperiencePrioritiesOptions: SurveyCustomerExperienceOptions[];
}

const AppointmentProposalSurveyOptionsContext = createContext<
  AppointmentProposalSurveyOptionsContextProps | undefined
>(undefined);

export const AppointmentProposalSurveyOptionsProvider: React.FC<
  PropsWithChildren<AppointmentProposalSurveyOptionsProviderProps>
> = ({
  children,
  currentCustomerExperienceOptions,
  customerExperiencePrioritiesOptions,
}) => {
  const [state, dispatch] = useReducer(
    proposalBuilderSurveyOptionsReducer,
    initialState
  );
  const isOnline = useNetworkStatus();
  const { showToast } = useToast();
  const httpClient = useHttpClient();

  useEffect(() => {
    state.error && showToast(`Error fetching files: ${state.error}`, 'error');
  }, [showToast, state.error]);

  const fetchProposalBuilderData = useCallback(
    async (organizationId: string) => {
      if (!isOnline()) {
        dispatch({
          type: 'SET_LOADING_STATE',
          payload: { isLoading: false, isLoaded: true },
        });
        return;
      }
      dispatch({ type: 'FETCH_SURVEY_OPTIONS' });

      try {
        const rapportOptions = await httpClient.get<OrganizationRapport[]>(
          `/organizations/${organizationId}/rapport`
        );

        const payload = {
          customerExperienceOptions: {
            current: transformRapportOptions(
              rapportOptions,
              'EXPERIENCE',
              currentCustomerExperienceOptions
            ),
            priorities: transformRapportOptions(
              rapportOptions,
              'PREFERENCES',
              customerExperiencePrioritiesOptions
            ),
          },
          mediaChecklistOptions: {
            indoor: mediaChecklistIndoorOptions,
            outdoor: mediaChecklistOutdoorOptions,
          },
        };

        dispatch({ type: 'FETCH_SURVEY_OPTIONS_SUCCESS', payload });
      } catch (error) {
        dispatch({
          type: 'FETCH_SURVEY_OPTIONS_FAILURE',
          payload: { error: `${error}` },
        });
      }
    },
    [
      httpClient,
      isOnline,
      currentCustomerExperienceOptions,
      customerExperiencePrioritiesOptions,
    ]
  );

  return (
    <AppointmentProposalSurveyOptionsContext.Provider
      value={{
        fetchProposalBuilderData,
        customerExperienceOptions: state.customerExperienceOptions,
        mediaChecklistOptions: state.mediaChecklistOptions,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
      }}
    >
      {children}
    </AppointmentProposalSurveyOptionsContext.Provider>
  );
};

export const useAppointmentProposalSurveyOptions = () => {
  const context = useContext(AppointmentProposalSurveyOptionsContext);
  if (!context) {
    throw new Error(
      'useAppointmentProposalSurveyOptions must be used within a AppointmentProposalSurveyOptionsProvider'
    );
  }
  return context;
};
